import { useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import ServiceModal from '../modals/ServiceModal';
import apiRequest from '../utils/api';
import { ServiceWithLocalization, LocalizedService } from '../types/Service';
import { useUserLocation } from '../utils/locationUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { APIProvider, Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import CategoryFilterDropdown from './CategoryDropdown';
import { markerIcons } from '../utils/serviceCategoryUtils';
import { getOffsetPositions } from '../utils/offsetLocation';
import HeatMapCircles from "./HeatMapCircles";
import { useUser } from '../contexts/UserContext';

const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const mapsId = process.env.REACT_APP_GOOGLE_MAPS_ID;

const ServiceMap = () => {
  const { translations } = useLanguage();

  if (!apiKey) {
    return <div>{translations.errors.maperror}</div>;
  }

  return (
    <APIProvider apiKey={apiKey} libraries={['geometry']}>
      <ServiceMapContent />
    </APIProvider>
  );
};

const ServiceMapContent = () => {
  const { language, translations } = useLanguage();
  const { userLatitude, userLongitude } = useUserLocation();
  const [services, setServices] = useState<LocalizedService[]>([]);
  const [selectedService, setSelectedService] = useState<LocalizedService>();
  const [isServiceModalOpen, setServiceModalOpen] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [zoomLevel, setZoomLevel] = useState<number>(5);
  const [closeCategoryDropdown, setCloseCategoryDropdown] = useState(false);
  const [heatMapData, setHeatMapData] = useState<{ lat: number; lng: number; count: number }[]>([]);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const { user } = useUser();
  const map = useMap();

  // Select the language from context
  const languageMap: Record<string, string> = {
    finnish: 'fi',
    english: 'en',
    swedish: 'sv'
  };
  const selectedLanguage = languageMap[language];

  // Fetch the services and localize them with the selected language
  const fetchServices = useCallback(async () => {
    try {
      const response = await apiRequest('/api/services');
      if (!response.ok) {
        throw new Error("error");
      }

      const data = await response.json();

      const localizedServices: LocalizedService[] = data.map((service: ServiceWithLocalization) => {
        const loc = service.localizations.find(l => l.language === selectedLanguage)
          || service.localizations.find(l => l.language === 'en')
          || service.localizations.find(l => l.language === 'fi');

        return {
          ...service,
          name: loc?.name || '',
          description: loc?.description || '',
          url: loc?.url || ''
        };
      });

      setServices(localizedServices);
    } catch (error) {
      console.error(error);
    }
  }, [selectedLanguage]);

  // Fetch HeatMap Data
  const fetchHeatMapData = async () => {
    try {
      const response = await apiRequest(`/api/user/heatmap`);
      const json = await response.json();
      setHeatMapData(json);
    } catch (err) {
      console.error("Error fetching heatmap data:", err);
    }
  };

  useEffect(() => {
    fetchServices();
    if (user) {
      fetchHeatMapData();
    }
  }, [fetchServices, user]);

  useEffect(() => {
    if (!map) return;

    const closeDropdown = () => setCloseCategoryDropdown(true);

    const clickListener = map.addListener('click', closeDropdown);
    const dragListener = map.addListener('dragstart', closeDropdown);
    const zoomListener = map.addListener('zoom_changed', closeDropdown);

    return () => {
      clickListener.remove();
      dragListener.remove();
      zoomListener.remove();
    };
  }, [map]);

  if (!apiKey) {
    return <div>error</div>;
  }

  const handleMarkerClick = (service: LocalizedService) => {
    setSelectedService(service);
    setServiceModalOpen(true);
  };

  const handleZoomChange = (map: google.maps.Map | null) => {
    if (map) {
      const newZoom = map.getZoom() ?? 10;
      setZoomLevel(newZoom);
      if (newZoom > 8) {
        setShowHeatmap(false);
      }
    }
  };

  const filteredServices = services.filter(s => selectedCategories.includes(s.category));

  const offsetPositions = getOffsetPositions(filteredServices);

  return (
    <div className='content service-map-content'>
      <div className="map-controls">
        {/* HeatMap */}
        <div className="heatmap-control">
          <label className={`checkbox-container ${zoomLevel > 8 ? 'disabled' : ''}`}>
            <input 
              type="checkbox" 
              checked={showHeatmap} 
              onChange={() => setShowHeatmap((prev) => !prev)} 
              disabled={zoomLevel > 8}
            />
            <div className='checkmark show-all'></div>
            <p className='show-all-text'>{translations.servicemap.heatmap}</p>
          </label>
        </div>

        {/* Services */}
        <CategoryFilterDropdown
          shouldClose={closeCategoryDropdown}
          onCloseHandled={() => setCloseCategoryDropdown(false)}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
        />
      </div>

      <div className='card service-map'>
        <Map
          defaultZoom={5}
          defaultCenter={{lat: 63.9241, lng: 25.7482}}
          mapId={mapsId}
          className='map'
          streetViewControl={false}
          fullscreenControl={false}
          onZoomChanged={() => handleZoomChange(map)}
        >
          {userLatitude != null && userLongitude != null && (
            <AdvancedMarker position={{ lat: userLatitude, lng: userLongitude }}>
              <div className='own-location-marker'>
                <div className='own-location-marker-outer' />
                <div className='own-location-marker-inner' />
              </div>
            </AdvancedMarker>
          )}
          {filteredServices.map((service) => ( 
            <AdvancedMarker
              key={service.service_id}
              position={offsetPositions.get(service.service_id)!}
              onClick={() => handleMarkerClick(service)}
            >
              <div className='marker-wrapper'>
                <img src={markerIcons[service.category]} width={48} height={48} alt='Category Icon' />
                {zoomLevel > 8 && (
                  <span className='marker-text'>{service.name}</span>
                )}
              </div>
            </AdvancedMarker>
          ))}
          {showHeatmap && <HeatMapCircles data={heatMapData}/>}
        </Map>
      </div>

      <Modal 
        isOpen={isServiceModalOpen} 
        onRequestClose={() => setServiceModalOpen(false)} 
        ariaHideApp={false} 
        className="modal"  
      >
        {selectedService && (
          <ServiceModal 
            service={selectedService}
            onClose={() => setServiceModalOpen(false)} 
          />
        )}
      </Modal>
    </div>
  );
};

export default ServiceMap;